// Slice B2.5 of docs/planning/activity_dock_workflow_facilitation.md:
// per-mode copy variants for the unsent-leads dock section.
//
// Background: through B2.4 the dock peek decision became mode-aware,
// so the *fire* of the unsent-leads peek is now restricted to `leads` /
// `salesConsole`. The *content* of the section, though, was still
// brand-anchored and mode-blind ("{n} waiting for GHL") regardless of
// where the user opened the dock from. B2.5 introduces a copy lever
// (not a visibility lever — the dock stays glanceable cross-mode) so
// the section's body line speaks the user's current mental model:
//
//   - `leads`: the user is mid-capture. The salient framing is
//     "captured but not sent" — emphasizes the workflow step they're
//     about to take with the Send-all CTA right next to it.
//   - `salesConsole`: the user is managing pipeline. The salient
//     framing is "not in your pipeline yet" — explains why these
//     captured leads aren't showing up among the funnel rows they're
//     working through.
//   - any other mode (or `null` / `undefined`): brand-neutral
//     "not yet in your CRM" framing. Still actionable, doesn't presume
//     why the user opened the dock. (Was "GHL" prior to dev-sync note
//     8c0af1c1 — Dean asked to switch user-facing strings to the
//     generic CRM acronym so the copy survives non-GHL backends.)
//
// Kept pure (no React, no DOM) so the policy lives in one file and the
// component just renders the result.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

/**
 * Returns the body-line string the unsent-leads section renders below
 * its "Unsent leads" heading. `count` is the total unsent figure;
 * `mode` shapes the framing.
 */
export function formatUnsentBodyLine(
  count: number,
  mode: PrimaryMode | null | undefined,
): string {
  switch (mode) {
    case "leads":
      return `${count} captured but not sent`;
    case "salesConsole":
      return `${count} not in your pipeline yet`;
    default:
      return `${count} not yet in your CRM`;
  }
}
